import { useState } from "react";
import { Loader2, UserPlus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function VoiceCloneDialog({ onCloned }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Donnez un nom à la voix."); return; }
    if (files.length === 0) { toast.error("Ajoutez au moins un extrait audio (mp3/wav, 30 s à 3 min conseillés)."); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append("name", name.trim());
    fd.append("description", description.trim());
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/elevenlabs/voices/clone", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Voix « ${data.name} » clonée ✨`);
      setOpen(false);
      setName(""); setDescription(""); setFiles([]);
      onCloned?.(data.voice_id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Clonage impossible.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 border-secondary/40 text-secondary hover:bg-secondary/10 gap-1.5" data-testid="clone-voice-open">
          <UserPlus size={14} /> Cloner une voix
        </Button>
      </DialogTrigger>
      <DialogContent className="glass border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><UserPlus className="text-secondary" size={20} /> Instant Voice Cloning</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nom de la voix</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Narrateur Kael" className="bg-background border-border" data-testid="clone-voice-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Description (optionnel)</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Voix grave, calme, narration fantasy" className="bg-background border-border" data-testid="clone-voice-description" />
          </div>
          <div className="space-y-1.5">
            <Label>Extraits audio</Label>
            <label className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-background p-5 cursor-pointer hover:border-secondary/50 transition-colors">
              <Upload size={18} className="text-secondary" />
              <span className="text-xs text-muted-foreground text-center">
                {files.length ? `${files.length} fichier(s) — ${files.map((f) => f.name).join(", ").slice(0, 60)}` : "Cliquez pour choisir des fichiers mp3 / wav (1 à 25 extraits)"}
              </span>
              <input type="file" accept="audio/*" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="clone-voice-files" />
            </label>
          </div>
          <Button onClick={submit} disabled={loading} className="w-full bg-secondary text-white hover:bg-secondary/90 gap-2" data-testid="clone-voice-submit">
            {loading ? <Loader2 className="animate-spin" size={15} /> : <UserPlus size={15} />} Cloner avec ElevenLabs
          </Button>
          <p className="text-[11px] text-muted-foreground">Utilisez uniquement des voix dont vous détenez les droits. Le clonage instantané ne consomme aucun crédit MusicFlow (facturé sur votre compte ElevenLabs).</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
